import React, { useState, useEffect, useCallback } from 'react';
import { WebCryptoRamManager, RamKeyPair } from '../crypto/webcrypto-ram';
import {
  InterventionRecipe,
  createDefaultRecipeForDomain,
} from '../sync/recipe-types';
import { IndividualRecipeBuilder } from './IndividualRecipeBuilder';
import { InteractiveBreakSimulator } from './InteractiveBreakSimulator';
import { StripeCustomerPortalModal } from './StripeCustomerPortalModal';
import { QRCodeSvg } from './QRCodeSvg';

export interface IndividualDashboardViewProps {
  cryptoManager: WebCryptoRamManager;
  onOpenStripePortal?: () => void;
  onNavigateHome?: () => void;
}

export interface PairingPayload {
  scheme: string;
  userId: string;
  publicKeyPem: string;
  keyFingerprint: string;
  pinToken: string;
  issuedAt: string;
  expiresAt: string;
}

export const IndividualDashboardView: React.FC<IndividualDashboardViewProps> = ({
  cryptoManager,
  onOpenStripePortal,
  onNavigateHome,
}) => {
  // 1. Subscription & Account State
  const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('annual');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('Active');

  // 2. Active Break Recipe & Studio/Simulator Tab
  const [activeRecipe, setActiveRecipe] = useState<InterventionRecipe>(() =>
    createDefaultRecipeForDomain('financial_speculation', 'self_user', 'My Personal Focus Shield')
  );
  const [activeStudioTab, setActiveStudioTab] = useState<'builder' | 'simulator'>('builder');

  // 3. Device Pairing State
  const [pairingPin, setPairingPin] = useState<string>('847291');
  const [keyPair, setKeyPair] = useState<RamKeyPair | null>(null);
  const [pairingPayload, setPairingPayload] = useState<PairingPayload | null>(null);
  const [pairingTimeRemaining, setPairingTimeRemaining] = useState<number>(900); // 15 minutes
  const [pairingStatus, setPairingStatus] = useState<'waiting' | 'paired'>('waiting');
  const [pairedDeviceName, setPairedDeviceName] = useState<string | null>(null);
  const [copyPinNotice, setCopyPinNotice] = useState<boolean>(false);
  const [copyTokenNotice, setCopyTokenNotice] = useState<boolean>(false);

  // 4. Local Journal Export State
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  // Generate Zero-Knowledge Device Pairing Session
  const generatePairingSession = useCallback(async () => {
    setPairingStatus('waiting');
    setPairedDeviceName(null);
    setPairingTimeRemaining(900);

    const keys = cryptoManager.getActiveKeyPair() || (await cryptoManager.generateKeyPair(2048));
    setKeyPair(keys);

    const randomPin = Math.floor(100000 + Math.random() * 900000).toString();
    setPairingPin(randomPin);

    const payload: PairingPayload = {
      scheme: 'luminousbreak://pair-device',
      userId: 'usr_individual_alex_m',
      publicKeyPem: keys.publicKeyPem,
      keyFingerprint: keys.keyFingerprint,
      pinToken: randomPin,
      issuedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    };
    setPairingPayload(payload);
  }, [cryptoManager]);

  useEffect(() => {
    generatePairingSession();
  }, [generatePairingSession]);

  // 15-minute countdown for pairing PIN
  useEffect(() => {
    const timer = setInterval(() => {
      setPairingTimeRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const handleCopyPin = () => {
    navigator.clipboard?.writeText(pairingPin);
    setCopyPinNotice(true);
    setTimeout(() => setCopyPinNotice(false), 2000);
  };

  const handleCopyToken = () => {
    if (!pairingPayload) return;
    navigator.clipboard?.writeText(JSON.stringify(pairingPayload, null, 2));
    setCopyTokenNotice(true);
    setTimeout(() => setCopyTokenNotice(false), 2000);
  };

  const handleSimulatePairingSuccess = () => {
    setPairingStatus('paired');
    setPairedDeviceName('iPhone 16 Pro (Luminous Break iOS App v1.4)');
  };

  // Launch Billing Portal
  const handleLaunchBilling = () => {
    if (onOpenStripePortal) {
      onOpenStripePortal();
    } else {
      setIsBillingModalOpen(true);
    }
  };

  // GDPR Article 20 Data Portability Export
  const handleExportJournal = () => {
    const exportBundle = {
      exportFormatVersion: '1.0',
      exportedAt: new Date().toISOString(),
      complianceStandard: 'GDPR Article 20 Data Portability & HIPAA Security Rule',
      encryptionScheme: 'AES-256-GCM + RSA-OAEP 2048',
      subscriber: {
        userId: 'usr_individual_alex_m',
        tier: 'Mindful Plus (Individual)',
        status: subscriptionStatus,
        billingInterval,
      },
      recipes: [activeRecipe],
      encryptedJournalEnvelopes: [
        {
          id: 'jnl_local_enc_001',
          timestamp: Date.now() - 3600000 * 2,
          domain: activeRecipe.domainId,
          ciphertextBase64: 'U2FtcGxlRW5jcnlwdGVkTG9nUGF5bG9hZA==',
          ivBase64: 'YWJjZGVmZ2hpamts',
          tagBase64: 'bW5vcHFyc3R1dnd4',
          aclMode: 'completely_isolated',
          zeroPlaintextGuarantee: true,
        },
      ],
      zeroEphiVerification: {
        stripeCustomerMetadataContainsPhi: false,
        cloudServerPlaintextLogs: false,
      },
    };

    const blob = new Blob([JSON.stringify(exportBundle, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `luminous_break_encrypted_journal_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    setExportNotice('✓ Encrypted journal export downloaded (GDPR Art. 20 Data Portability compliant).');
    setTimeout(() => setExportNotice(null), 4000);
  };

  return (
    <div
      className="individual-dashboard-root"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Top Welcome & Summary Header */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '24px' }}>🌱</span>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1E293B', letterSpacing: '-0.3px' }}>
              Individual Subscriber Workspace
            </h2>
            <span
              style={{
                backgroundColor: '#DCFCE7',
                color: '#166534',
                padding: '3px 10px',
                borderRadius: '16px',
                fontSize: '11px',
                fontWeight: 700,
                border: '1px solid #86EFAC',
              }}
            >
              ● {subscriptionStatus}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
            Personal neuro-grounding cockpit • Zero-Knowledge local journal privacy • Multi-device synchronization.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            type="button"
            onClick={handleExportJournal}
            style={{
              backgroundColor: '#FAF8F5',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#334155',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            📥 Export My Encrypted Journal
          </button>

          <button
            type="button"
            onClick={handleLaunchBilling}
            style={{
              backgroundColor: '#5E8276',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: 700,
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 6px rgba(94,130,118,0.3)',
            }}
          >
            💳 Manage Billing & Invoices
          </button>
        </div>
      </div>

      {exportNotice && (
        <div
          role="status"
          style={{
            backgroundColor: '#DCFCE7',
            color: '#166534',
            border: '1px solid #86EFAC',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {exportNotice}
        </div>
      )}

      {/* SECTION 1: Subscription Tier Details & Billing Card */}
      <div
        className="subscription-tier-details-card"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#1E293B' }}>
            Subscription & Payment Plan
          </h3>
          <span style={{ fontSize: '12px', color: '#64748B' }}>Self-Service Customer Portal via Stripe</span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            backgroundColor: '#FAF8F5',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            marginBottom: '16px',
          }}
        >
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '4px' }}>
              Current Plan
            </div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#1E293B' }}>
              Mindful Plus (Individual)
            </div>
            <div style={{ fontSize: '12px', color: '#5E8276', fontWeight: 600, marginTop: '2px' }}>
              Full 9-Domain Protection + Cross-Device Sync
            </div>
          </div>

          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '4px' }}>
              Billing Cycle
            </div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#1E293B' }}>
              {billingInterval === 'annual' ? '$59.88 / year ($4.99/mo)' : '$7.99 / month'}
            </div>
            <div style={{ fontSize: '11px', color: '#166534', fontWeight: 700, marginTop: '2px' }}>
              {billingInterval === 'annual' ? '★ Annual Saver (38% discount)' : 'Monthly Flexible'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '4px' }}>
              Payment Method
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: '6px' }}>
              💳 Visa ending in 4242
            </div>
            <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
              Next renewal: September 21, 2027
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleLaunchBilling}
              style={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                border: '1.5px solid #5E8276',
                color: '#5E8276',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              Update Card or Change Tier →
            </button>
          </div>
        </div>

        {/* Zero-ePHI Isolation Badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '12px',
            color: '#166534',
          }}
        >
          <span>🛡️</span>
          <span>
            <strong>Zero-ePHI Billing Isolation Guarantee:</strong> Your Stripe billing record contains only non-clinical licensing identifiers.
            Zero impulse logs, website targets, or journal thoughts are ever shared with payment processors.
          </span>
        </div>
      </div>

      {/* SECTION 2: Composable Recipe Studio & Real-Time Simulator */}
      <div
        className="recipe-studio-simulator-section"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            borderBottom: '1px solid #E2E8F0',
            paddingBottom: '16px',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1E293B' }}>
              Personal Break Intervention Studio & Simulator
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
              Tailor your multi-block interventions and test them in real-time with anti-bypass verification.
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              backgroundColor: '#FAF8F5',
              padding: '4px',
              borderRadius: '10px',
              border: '1px solid #CBD5E1',
            }}
          >
            <button
              type="button"
              onClick={() => setActiveStudioTab('builder')}
              style={{
                border: 'none',
                backgroundColor: activeStudioTab === 'builder' ? '#5E8276' : 'transparent',
                color: activeStudioTab === 'builder' ? '#FFFFFF' : '#64748B',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              🎨 Recipe Builder
            </button>
            <button
              type="button"
              onClick={() => setActiveStudioTab('simulator')}
              style={{
                border: 'none',
                backgroundColor: activeStudioTab === 'simulator' ? '#0F172A' : 'transparent',
                color: activeStudioTab === 'simulator' ? '#FFFFFF' : '#64748B',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              ▶ Real-Time Break Simulator
            </button>
          </div>
        </div>

        {activeStudioTab === 'builder' ? (
          <IndividualRecipeBuilder
            initialDomain={activeRecipe.domainId}
            initialRecipe={activeRecipe}
            onSaveRecipe={(saved) => setActiveRecipe(saved)}
            onPreviewRecipe={(updated) => setActiveRecipe(updated)}
          />
        ) : (
          <InteractiveBreakSimulator
            recipe={activeRecipe}
            onCompleteSession={() => {}}
          />
        )}
      </div>

      {/* SECTION 3: Device Pairing PIN & Zero-Knowledge Public Key Exchange */}
      <div
        className="device-pairing-pin-section"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#1E293B' }}>
              Device Pairing & Zero-Knowledge Key Exchange
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
              Pair your iOS/Android app and Safari/Chrome extensions without sending cryptographic private keys over the wire.
            </p>
          </div>

          <button
            type="button"
            onClick={generatePairingSession}
            style={{
              backgroundColor: '#FAF8F5',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            ↺ Generate New PIN
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            alignItems: 'center',
          }}
        >
          {/* Left: PIN & Expiration */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div
              style={{
                backgroundColor: '#FAF8F5',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                One-Time Device Pairing PIN
              </div>
              <div
                style={{
                  fontSize: '36px',
                  fontWeight: 900,
                  letterSpacing: '6px',
                  color: '#1E293B',
                  fontFamily: 'monospace',
                  marginBottom: '8px',
                }}
              >
                {pairingPin}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: '#B45309', fontWeight: 600 }}>
                  ⏱ Expires in {formatCountdown(pairingTimeRemaining)}
                </span>
                <button
                  type="button"
                  onClick={handleCopyPin}
                  style={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #CBD5E1',
                    borderRadius: '6px',
                    padding: '3px 10px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#475569',
                    cursor: 'pointer',
                  }}
                >
                  {copyPinNotice ? '✓ Copied!' : 'Copy PIN'}
                </button>
              </div>
            </div>

            {/* Cryptographic Public Key Fingerprint */}
            <div
              style={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '11.5px',
                color: '#475569',
              }}
            >
              <div style={{ fontWeight: 700, color: '#1E293B', marginBottom: '4px' }}>
                RSA-2048 Public Key Fingerprint (Volatile RAM):
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all', color: '#0369A1' }}>
                {keyPair?.keyFingerprint || 'SHA256:7a:3b:91:c2:d8:44:e1:09:aa:ff:12:80:bb:ee:33:99'}
              </div>
              <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '10.5px', color: '#64748B' }}>Curve: RSA-OAEP 2048 • SHA-256</span>
                <button
                  type="button"
                  onClick={handleCopyToken}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#0369A1',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {copyTokenNotice ? '✓ Copied Token' : 'Copy Exchange JSON'}
                </button>
              </div>
            </div>

            {/* Pairing Status Banner & Simulation Button */}
            <div
              style={{
                backgroundColor: pairingStatus === 'paired' ? '#DCFCE7' : '#EFF6FF',
                border: pairingStatus === 'paired' ? '1px solid #86EFAC' : '1px solid #BFDBFE',
                borderRadius: '10px',
                padding: '12px 14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: pairingStatus === 'paired' ? '#166534' : '#1E40AF' }}>
                  {pairingStatus === 'paired' ? '✓ Device Paired Successfully' : 'Waiting for Device Scan...'}
                </div>
                <div style={{ fontSize: '11px', color: pairingStatus === 'paired' ? '#15803D' : '#3B82F6' }}>
                  {pairingStatus === 'paired' ? pairedDeviceName : 'Enter PIN or scan QR code in the iOS/Android app'}
                </div>
              </div>

              {pairingStatus !== 'paired' && (
                <button
                  type="button"
                  onClick={handleSimulatePairingSuccess}
                  style={{
                    backgroundColor: '#1E40AF',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Simulate Pairing
                </button>
              )}
            </div>
          </div>

          {/* Right: QR Code Visual */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <QRCodeSvg
              value={pairingPayload ? JSON.stringify(pairingPayload) : 'luminousbreak://pair-device'}
              size={180}
              fgColor="#1E293B"
            />
            <span style={{ fontSize: '11px', color: '#64748B', marginTop: '8px', textAlign: 'center' }}>
              Scan with Luminous Break Mobile Camera
            </span>
          </div>
        </div>
      </div>

      {/* SECTION 4: In-App Zero-Knowledge Data Protection & Local Journal Storage Explanations */}
      <div
        className="zero-knowledge-explanations-section"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🔐</span>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#1E293B' }}>
              Zero-Knowledge Privacy Architecture & Local Journal Storage
            </h3>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
            How Luminous Break guarantees sovereign data protection: your mental health reflections and habit triggers never reach our servers in plaintext.
          </p>
        </div>

        {/* 4 Pillars Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          {/* Pillar 1 */}
          <div
            style={{
              backgroundColor: '#FAF8F5',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '24px' }}>💻</span>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
              1. Client-Side Cryptography
            </div>
            <p style={{ margin: 0, fontSize: '11.5px', color: '#64748B', lineHeight: '1.45' }}>
              All encryption keys (AES-256-GCM and RSA-2048) are generated directly in your browser or device’s volatile RAM. Private keys never touch cloud disks or external networks.
            </p>
          </div>

          {/* Pillar 2 */}
          <div
            style={{
              backgroundColor: '#FAF8F5',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '24px' }}>📓</span>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
              2. Local-First Journal Storage
            </div>
            <p style={{ margin: 0, fontSize: '11.5px', color: '#64748B', lineHeight: '1.45' }}>
              Your CBT thoughts, craving ratings, and break reflections reside in encrypted on-device SQLite / IndexedDB vaults. Zero plaintext habit tracking logs are ever sent to remote databases.
            </p>
          </div>

          {/* Pillar 3 */}
          <div
            style={{
              backgroundColor: '#FAF8F5',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '24px' }}>🔄</span>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
              3. Zero-Knowledge Cloud Sync
            </div>
            <p style={{ margin: 0, fontSize: '11.5px', color: '#64748B', lineHeight: '1.45' }}>
              When syncing recipes across paired phones and laptops, only encrypted envelopes with client-held keys are transmitted. Even Luminous Break engineers cannot decrypt your reflections.
            </p>
          </div>

          {/* Pillar 4 */}
          <div
            style={{
              backgroundColor: '#FAF8F5',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '24px' }}>🛡️</span>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
              4. Zero-ePHI Stripe Guarantee
            </div>
            <p style={{ margin: 0, fontSize: '11.5px', color: '#64748B', lineHeight: '1.45' }}>
              Stripe and financial processors only see anonymous billing tokens and your subscription SKU. No diagnostic information or target URLs are ever included in payment metadata.
            </p>
          </div>
        </div>

        {/* Visual Privacy Flow Architecture Diagram */}
        <div
          style={{
            backgroundColor: '#0F172A',
            borderRadius: '12px',
            padding: '20px',
            color: '#F8FAFC',
            border: '1px solid #1E293B',
          }}
        >
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#10B981', textTransform: 'uppercase', marginBottom: '10px' }}>
            Visual End-to-End Privacy Data Flow:
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              fontSize: '11.5px',
              fontFamily: 'monospace',
            }}
          >
            <div style={{ backgroundColor: '#1E293B', padding: '10px 14px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
              <div style={{ color: '#60A5FA', fontWeight: 700 }}>Your Device (Browser / App)</div>
              <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>Plaintext Thought + AES-256 Key</div>
            </div>

            <span style={{ color: '#10B981', fontSize: '16px' }}>➔</span>

            <div style={{ backgroundColor: '#1E293B', padding: '10px 14px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
              <div style={{ color: '#F59E0B', fontWeight: 700 }}>Local Encrypted Vault</div>
              <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>Ciphertext + GMAC Auth Tag</div>
            </div>

            <span style={{ color: '#10B981', fontSize: '16px' }}>➔</span>

            <div style={{ backgroundColor: '#1E293B', padding: '10px 14px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
              <div style={{ color: '#A78BFA', fontWeight: 700 }}>Blind Relay Sync Server</div>
              <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>Zero Decryption Keys Held</div>
            </div>

            <span style={{ color: '#10B981', fontSize: '16px' }}>➔</span>

            <div style={{ backgroundColor: '#1E293B', padding: '10px 14px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
              <div style={{ color: '#34D399', fontWeight: 700 }}>Paired Mobile Device</div>
              <div style={{ color: '#94A3B8', fontSize: '10.5px' }}>Decrypted Locally in RAM</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stripe Customer Portal Modal */}
      <StripeCustomerPortalModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        initialInterval={billingInterval}
        onPlanChanged={(newInterval) => setBillingInterval(newInterval)}
        onSubscriptionCancelled={() => setSubscriptionStatus('Cancelled (Active until period end)')}
      />
    </div>
  );
};

export default IndividualDashboardView;
